import React, { cloneElement, memo, useContext, useMemo, useRef } from "react";
import { ChildDescriptorsContext } from "../contexts/ChildDescriptorsContext.js";
import { IgnoreMutationContext } from "../contexts/IgnoreMutationContext.js";
import { NodeViewContext } from "../contexts/NodeViewContext.js";
import { SelectNodeContext } from "../contexts/SelectNodeContext.js";
import { StopEventContext } from "../contexts/StopEventContext.js";
import { useNodeViewDescriptor } from "../hooks/useNodeViewDescriptor.js";
import { ChildNodeViews, wrapInDeco } from "./ChildNodeViews.js";
import { OutputSpec } from "./OutputSpec.js";
export const ReactNodeView = /*#__PURE__*/ memo(function ReactNodeView(param) {
    let { outerDeco, getPos, node, innerDeco, ...props } = param;
    const domRef = useRef(null);
    const nodeDomRef = useRef(null);
    const contentDomRef = useRef(null);
    const getPosFunc = useRef(()=>getPos.current()).current;
    const { nodeViews } = useContext(NodeViewContext);
    let element = null;
    const Component = nodeViews[node.type.name];
    const outputSpec = useMemo(()=>node.type.spec.toDOM?.(node), [
        node
    ]);
    const { hasContentDOM, childDescriptors, setStopEvent, setSelectNode, setIgnoreMutation, nodeViewDescRef } = useNodeViewDescriptor(node, ()=>getPos.current(), domRef, nodeDomRef, innerDeco, outerDeco, undefined, contentDomRef);
    const finalProps = {
        ...props,
        ...!hasContentDOM && nodeDomRef.current?.tagName !== "BR" && {
            contentEditable: false
        }
    };
    const nodeProps = useMemo(()=>({
            node: node,
            getPos: getPosFunc,
            decorations: outerDeco,
            innerDecorations: innerDeco
        }), [
        getPosFunc,
        innerDeco,
        node,
        outerDeco
    ]);
    if (Component) {
        element = /*#__PURE__*/ React.createElement(Component, {
            ...finalProps,
            ref: nodeDomRef,
            nodeProps: nodeProps
        }, /*#__PURE__*/ React.createElement(ChildNodeViews, {
            getPos: getPos,
            node: node,
            innerDecorations: innerDeco
        }));
    } else {
        if (outputSpec) {
            element = /*#__PURE__*/ React.createElement(OutputSpec, {
                ...finalProps,
                ref: nodeDomRef,
                outputSpec: outputSpec
            }, /*#__PURE__*/ React.createElement(ChildNodeViews, {
                getPos: getPos,
                node: node,
                innerDecorations: innerDeco
            }));
        }
    }
    if (!element) {
        throw new Error(`Node spec for ${node.type.name} is missing toDOM`);
    }
    const decoratedElement = /*#__PURE__*/ cloneElement(outerDeco.reduce(wrapInDeco, element), // eslint-disable-next-line @typescript-eslint/no-explicit-any
    outerDeco.some((d)=>d.type.attrs.nodeName) ? {
        ref: domRef
    } : // we've already passed the domRef to the NodeView component
    // as a prop
    undefined);
    const childContextValue = useMemo(()=>({
            parentRef: nodeViewDescRef,
            siblingsRef: childDescriptors
        }), [
        childDescriptors,
        nodeViewDescRef
    ]);
    return /*#__PURE__*/ React.createElement(SelectNodeContext.Provider, {
        value: setSelectNode
    }, /*#__PURE__*/ React.createElement(StopEventContext.Provider, {
        value: setStopEvent
    }, /*#__PURE__*/ React.createElement(IgnoreMutationContext.Provider, {
        value: setIgnoreMutation
    }, /*#__PURE__*/ React.createElement(ChildDescriptorsContext.Provider, {
        value: childContextValue
    }, decoratedElement))));
});
